/*@jsxRuntime automatic @jsxImportSource react*/
import {Fragment as _Fragment, jsx as _jsx, jsxs as _jsxs} from "react/jsx-runtime";
import {useMDXComponents as _provideComponents} from "@mdx-js/react";
import {Docs} from 'components/layout';
import {getIntegrations} from 'api/get-integrations';
import {getExtensions} from 'api/get-extensions';
import {Warning, Tip, Info, WhitelistDetails} from 'ui';
export const title = "Consent Manager", description = "Hightouch Events Consent Manager UI", lastUpdatedDate = new Date(1711065600000);
import {Tabs, TabList, TabPanels, Tab, TabPanel} from '@chakra-ui/react';
export async function getStaticProps() {
  const integrations = await getIntegrations();
  const extensions = await getExtensions();
  return {
    props: {
      ...integrations,
      extensions,
      meta: {
        title: "Consent Manager",
        description: "Hightouch Events Consent Manager UI",
        lastUpdatedDate: "Fri Mar 22 2024 00:00:00 GMT+0000 (Coordinated Universal Time)",
        slug: null,
        type: null,
        icon: null,
        toc: [{
          "value": "Prerequisites",
          "depth": 0,
          "id": "prerequisites"
        }, {
          "value": "Installation",
          "depth": 0,
          "id": "installation"
        }, {
          "value": "API",
          "depth": 0,
          "id": "api"
        }]
      }
    },
    revalidate: 60
  };
}
const MDXLayout = ({children, ...props}) => _jsx(Docs, Object.assign({}, props, {
  children: children
}));
function MDXContent(props = {}) {
  return MDXLayout ? _jsx(MDXLayout, Object.assign({}, props, {
    children: _jsx(_createMdxContent, {})
  })) : _createMdxContent();
  function _createMdxContent() {
    const _components = Object.assign({
      p: "p",
      h2: "h2",
      code: "code",
      pre: "pre",
      span: "span",
      a: "a"
    }, _provideComponents(), props.components);
    return _jsxs(_Fragment, {
      children: [_jsx(_components.p, {
        children: "Hightouch Events offers the Consent Manager UI for managing user consent and tracking preferences. Users can opt in/out of specific categories of data collection, and those preferences are synced to your configured destinations."
      }), "\n", _jsx(_components.h2, {
        id: "prerequisites",
        children: "Prerequisites"
      }), "\n", _jsx(_components.p, {
        children: "In order to use Consent Manager UI, you must use the Browser (Javascript) SDK snippet on your site."
      }), "\n", _jsxs(_components.p, {
        children: ["Note: ", _jsx(_components.code, {
          children: "e.load('WRITE_KEY')"
        }), " is removed from the snippet since the Consent Manager will load the Browser SDK for you."]
      }), "\n", _jsx(_components.pre, {
        children: _jsxs(_components.code, {
          className: "hljs language-html",
          children: [_jsx(_components.span, {
            className: "hljs-comment",
            children: "<!-- Hightouch Events Browser SDK snippet -->"
          }), "\n", _jsxs(_components.span, {
            className: "hljs-tag",
            children: ["<", _jsx(_components.span, {
              className: "hljs-name",
              children: "script"
            }), " ", _jsx(_components.span, {
              className: "hljs-attr",
              children: "type"
            }), "=", _jsx(_components.span, {
              className: "hljs-string",
              children: "\"text/javascript\""
            }), ">"]
          }), _jsxs(_components.span, {
            className: "javascript",
            children: ["\n!", _jsx(_components.span, {
              className: "hljs-keyword",
              children: "function"
            }), "(", _jsx(_components.span, {
              className: "hljs-params"
            }), "){", _jsx(_components.span, {
              className: "hljs-keyword",
              children: "var"
            }), " e=", _jsx(_components.span, {
              className: "hljs-variable language_",
              children: "window"
            }), ".", _jsx(_components.span, {
              className: "hljs-property",
              children: "htevents"
            }), "=", _jsx(_components.span, {
              className: "hljs-variable language_",
              children: "window"
            }), ".", _jsx(_components.span, {
              className: "hljs-property",
              children: "htevents"
            }), "||[];", _jsx(_components.span, {
              className: "hljs-keyword",
              children: "if"
            }), "(!e.", _jsx(_components.span, {
              className: "hljs-property",
              children: "initialize"
            }), ")", _jsx(_components.span, {
              className: "hljs-keyword",
              children: "if"
            }), "(e.", _jsx(_components.span, {
              className: "hljs-property",
              children: "invoked"
            }), ")", _jsx(_components.span, {
              className: "hljs-variable language_",
              children: "window"
            }), ".", _jsx(_components.span, {
              className: "hljs-property",
              children: "console"
            }), "&&", _jsx(_components.span, {
              className: "hljs-variable language_",
              children: "console"
            }), ".", _jsx(_components.span, {
              className: "hljs-property",
              children: "error"
            }), "&&", _jsx(_components.span, {
              className: "hljs-variable language_",
              children: "console"
            }), ".", _jsx(_components.span, {
              className: "hljs-title function_",
              children: "error"
            }), "(", _jsx(_components.span, {
              className: "hljs-string",
              children: "\"Hightouch snippet included twice.\""
            }), ");", _jsx(_components.span, {
              className: "hljs-keyword",
              children: "else"
            }), "{e.", _jsx(_components.span, {
              className: "hljs-property",
              children: "invoked"
            }), "=!", _jsx(_components.span, {
              className: "hljs-number",
              children: "0"
            }), ",e.", _jsx(_components.span, {
              className: "hljs-property",
              children: "methods"
            }), "=[", _jsx(_components.span, {
              className: "hljs-string",
              children: "\"trackSubmit\""
            }), ",", _jsx(_components.span, {
              className: "hljs-string",
              children: "\"trackClick\""
            }), ",", _jsx(_components.span, {
              className: "hljs-string",
              children: "\"trackLink\""
            }), ",", _jsx(_components.span, {
              className: "hljs-string",
              children: "\"trackForm\""
            }), ",", _jsx(_components.span, {
              className: "hljs-string",
              children: "\"pageview\""
            }), ",", _jsx(_components.span, {
              className: "hljs-string",
              children: "\"identify\""
            }), ",", _jsx(_components.span, {
              className: "hljs-string",
              children: "\"reset\""
            }), ",", _jsx(_components.span, {
              className: "hljs-string",
              children: "\"group\""
            }), ",", _jsx(_components.span, {
              className: "hljs-string",
              children: "\"track\""
            }), ",", _jsx(_components.span, {
              className: "hljs-string",
              children: "\"ready\""
            }), ",", _jsx(_components.span, {
              className: "hljs-string",
              children: "\"alias\""
            }), ",", _jsx(_components.span, {
              className: "hljs-string",
              children: "\"debug\""
            }), ",", _jsx(_components.span, {
              className: "hljs-string",
              children: "\"page\""
            }), ",", _jsx(_components.span, {
              className: "hljs-string",
              children: "\"once\""
            }), ",", _jsx(_components.span, {
              className: "hljs-string",
              children: "\"off\""
            }), ",", _jsx(_components.span, {
              className: "hljs-string",
              children: "\"on\""
            }), ",", _jsx(_components.span, {
              className: "hljs-string",
              children: "\"addSourceMiddleware\""
            }), ",", _jsx(_components.span, {
              className: "hljs-string",
              children: "\"addIntegrationMiddleware\""
            }), ",", _jsx(_components.span, {
              className: "hljs-string",
              children: "\"setAnonymousId\""
            }), ",", _jsx(_components.span, {
              className: "hljs-string",
              children: "\"addDestinationMiddleware\""
            }), "],e.", _jsx(_components.span, {
              className: "hljs-property",
              children: "factory"
            }), "=", _jsx(_components.span, {
              className: "hljs-keyword",
              children: "function"
            }), "(", _jsx(_components.span, {
              className: "hljs-params",
              children: "t"
            }), "){", _jsx(_components.span, {
              className: "hljs-keyword",
              children: "return"
            }), " ", _jsx(_components.span, {
              className: "hljs-keyword",
              children: "function"
            }), "(", _jsx(_components.span, {
              className: "hljs-params"
            }), "){", _jsx(_components.span, {
              className: "hljs-keyword",
              children: "var"
            }), " n=", _jsx(_components.span, {
              className: "hljs-title class_",
              children: "Array"
            }), ".", _jsx(_components.span, {
              className: "hljs-property",
              children: _jsx(_components.span, {
                className: "hljs-keyword",
                children: "prototype"
              })
            }), ".", _jsx(_components.span, {
              className: "hljs-property",
              children: "slice"
            }), ".", _jsx(_components.span, {
              className: "hljs-title function_",
              children: "call"
            }), "(", _jsx(_components.span, {
              className: "hljs-variable language_",
              children: "arguments"
            }), ");", _jsx(_components.span, {
              className: "hljs-keyword",
              children: "return"
            }), " n.", _jsx(_components.span, {
              className: "hljs-title function_",
              children: "unshift"
            }), "(t),e.", _jsx(_components.span, {
              className: "hljs-title function_",
              children: "push"
            }), "(n),e}};", _jsx(_components.span, {
              className: "hljs-keyword",
              children: "for"
            }), "(", _jsx(_components.span, {
              className: "hljs-keyword",
              children: "var"
            }), " t=", _jsx(_components.span, {
              className: "hljs-number",
              children: "0"
            }), ";t<e.", _jsx(_components.span, {
              className: "hljs-property",
              children: "methods"
            }), ".", _jsx(_components.span, {
              className: "hljs-property",
              children: "length"
            }), ";t++){", _jsx(_components.span, {
              className: "hljs-keyword",
              children: "var"
            }), " n=e.", _jsx(_components.span, {
              className: "hljs-property",
              children: "methods"
            }), "[t];e[n]=e.", _jsx(_components.span, {
              className: "hljs-title function_",
              children: "factory"
            }), "(n)}e.", _jsx(_components.span, {
              className: "hljs-property",
              children: "load"
            }), "=", _jsx(_components.span, {
              className: "hljs-keyword",
              children: "function"
            }), "(", _jsx(_components.span, {
              className: "hljs-params",
              children: "t,n"
            }), "){", _jsx(_components.span, {
              className: "hljs-keyword",
              children: "var"
            }), " o=", _jsx(_components.span, {
              className: "hljs-variable language_",
              children: "document"
            }), ".", _jsx(_components.span, {
              className: "hljs-title function_",
              children: "createElement"
            }), "(", _jsx(_components.span, {
              className: "hljs-string",
              children: "\"script\""
            }), ");o.", _jsx(_components.span, {
              className: "hljs-property",
              children: "type"
            }), "=", _jsx(_components.span, {
              className: "hljs-string",
              children: "\"text/javascript\""
            }), ",o.", _jsx(_components.span, {
              className: "hljs-property",
              children: "async"
            }), "=!", _jsx(_components.span, {
              className: "hljs-number",
              children: "0"
            }), ",o.", _jsx(_components.span, {
              className: "hljs-property",
              children: "src"
            }), "=", _jsx(_components.span, {
              className: "hljs-string",
              children: "\"https://cdn.hightouch-events.com/browser/release/v1-latest/events.min.js\""
            }), ";", _jsx(_components.span, {
              className: "hljs-keyword",
              children: "var"
            }), " r=", _jsx(_components.span, {
              className: "hljs-variable language_",
              children: "document"
            }), ".", _jsx(_components.span, {
              className: "hljs-title function_",
              children: "getElementsByTagName"
            }), "(", _jsx(_components.span, {
              className: "hljs-string",
              children: "\"script\""
            }), ")[", _jsx(_components.span, {
              className: "hljs-number",
              children: "0"
            }), "];r.", _jsx(_components.span, {
              className: "hljs-property",
              children: "parentNode"
            }), ".", _jsx(_components.span, {
              className: "hljs-title function_",
              children: "insertBefore"
            }), "(o,r),e.", _jsx(_components.span, {
              className: "hljs-property",
              children: "_loadOptions"
            }), "=n,e.", _jsx(_components.span, {
              className: "hljs-property",
              children: "_writeKey"
            }), "=t},e.", _jsx(_components.span, {
              className: "hljs-property",
              children: "SNIPPET_VERSION"
            }), "=", _jsx(_components.span, {
              className: "hljs-string",
              children: "\"0.0.1\""
            }), ",\ne.", _jsx(_components.span, {
              className: "hljs-title function_",
              children: "page"
            }), "()}}();\n"]
          }), _jsxs(_components.span, {
            className: "hljs-tag",
            children: ["</", _jsx(_components.span, {
              className: "hljs-name",
              children: "script"
            }), ">"]
          }), "\n"]
        })
      }), "\n", _jsx(_components.h2, {
        id: "installation",
        children: "Installation"
      }), "\n", _jsxs(_components.p, {
        children: ["The Consent Manager is available via ", _jsx(_components.a, {
          href: "https://npmjs.org/@ht-sdks/consent-manager",
          children: "NPM"
        }), ". It can be installed on any site using a ", _jsx(_components.code, {
          children: "<script>"
        }), " tag, we also offer React support."]
      }), "\n", "\n", _jsxs(Tabs, {
        children: [_jsxs(TabList, {
          children: [_jsx(Tab, {
            children: "HTML"
          }), "\n", _jsx(Tab, {
            children: "React"
          })]
        }), _jsxs(TabPanels, {
          children: [_jsx(TabPanel, {
            children: _jsx(_components.pre, {
              children: _jsxs(_components.code, {
                className: "hljs language-html",
                children: [_jsx(_components.span, {
                  className: "hljs-comment",
                  children: "<!-- Hightouch Consent Manager configuration -->"
                }), "\n", _jsxs(_components.span, {
                  className: "hljs-tag",
                  children: ["<", _jsx(_components.span, {
                    className: "hljs-name",
                    children: "script"
                  }), ">"]
                }), _jsxs(_components.span, {
                  className: "javascript",
                  children: ["\n  ", _jsx(_components.span, {
                    className: "hljs-variable language_",
                    children: "window"
                  }), ".", _jsx(_components.span, {
                    className: "hljs-property",
                    children: "consentManagerConfig"
                  }), " = ", _jsx(_components.span, {
                    className: "hljs-keyword",
                    children: "function"
                  }), "(", _jsx(_components.span, {
                    className: "hljs-params",
                    children: _jsx(_components.span, {
                      className: "hljs-built_in",
                      children: "exports"
                    })
                  }), ") {\n    ", _jsx(_components.span, {
                    className: "hljs-keyword",
                    children: "return"
                  }), " {\n      ", _jsx(_components.span, {
                    className: "hljs-attr",
                    children: "container"
                  }), ": ", _jsx(_components.span, {
                    className: "hljs-string",
                    children: "'#consent-manager'"
                  }), ",\n      ", _jsx(_components.span, {
                    className: "hljs-attr",
                    children: "writeKey"
                  }), ": ", _jsx(_components.span, {
                    className: "hljs-string",
                    children: "'WRITE_KEY'"
                  }), ",\n      ", _jsx(_components.span, {
                    className: "hljs-attr",
                    children: "shouldRequireConsent"
                  }), ": ", _jsx(_components.span, {
                    className: "hljs-built_in",
                    children: "exports"
                  }), ".", _jsx(_components.span, {
                    className: "hljs-property",
                    children: "inEU"
                  }), " ", _jsx(_components.span, {
                    className: "hljs-comment",
                    children: "// optional: require consent only for EU users"
                  }), "\n    }\n  }\n"]
                }), _jsxs(_components.span, {
                  className: "hljs-tag",
                  children: ["</", _jsx(_components.span, {
                    className: "hljs-name",
                    children: "script"
                  }), ">"]
                }), "\n\n", _jsx(_components.span, {
                  className: "hljs-comment",
                  children: "<!-- Hightouch Consent Manager JS -->"
                }), "\n", _jsxs(_components.span, {
                  className: "hljs-tag",
                  children: ["<", _jsx(_components.span, {
                    className: "hljs-name",
                    children: "script"
                  }), "\n  ", _jsx(_components.span, {
                    className: "hljs-attr",
                    children: "src"
                  }), "=", _jsx(_components.span, {
                    className: "hljs-string",
                    children: "\"https://unpkg.com/@ht-sdks/consent-manager@0.0.1/standalone/consent-manager.js\""
                  }), "\n  ", _jsx(_components.span, {
                    className: "hljs-attr",
                    children: "defer"
                  }), "\n>"]
                }), _jsxs(_components.span, {
                  className: "hljs-tag",
                  children: ["</", _jsx(_components.span, {
                    className: "hljs-name",
                    children: "script"
                  }), ">"]
                }), "\n"]
              })
            })
          }), _jsx(TabPanel, {
            children: _jsx(_components.pre, {
              children: _jsxs(_components.code, {
                className: "hljs language-jsx",
                children: [_jsx(_components.span, {
                  className: "hljs-keyword",
                  children: "import"
                }), " ", _jsx(_components.span, {
                  className: "hljs-title class_",
                  children: "React"
                }), " ", _jsx(_components.span, {
                  className: "hljs-keyword",
                  children: "from"
                }), " ", _jsx(_components.span, {
                  className: "hljs-string",
                  children: "'react'"
                }), "\n", _jsx(_components.span, {
                  className: "hljs-keyword",
                  children: "import"
                }), " { ", _jsx(_components.span, {
                  className: "hljs-title class_",
                  children: "ConsentManager"
                }), ", openConsentManager } ", _jsx(_components.span, {
                  className: "hljs-keyword",
                  children: "from"
                }), " ", _jsx(_components.span, {
                  className: "hljs-string",
                  children: "'@ht-sdks/consent-manager'"
                }), "\n\n", _jsx(_components.span, {
                  className: "hljs-keyword",
                  children: "export"
                }), " ", _jsx(_components.span, {
                  className: "hljs-keyword",
                  children: "default"
                }), " ", _jsx(_components.span, {
                  className: "hljs-keyword",
                  children: "function"
                }), " ", _jsx(_components.span, {
                  className: "hljs-title function_",
                  children: "App"
                }), "(", _jsx(_components.span, {
                  className: "hljs-params"
                }), ") {\n  ", _jsx(_components.span, {
                  className: "hljs-keyword",
                  children: "return"
                }), " (\n    ", _jsxs(_components.span, {
                  className: "xml",
                  children: [_jsxs(_components.span, {
                    className: "hljs-tag",
                    children: ["<", _jsx(_components.span, {
                      className: "hljs-name",
                      children: "div"
                    }), ">"]
                  }), "\n      ", _jsxs(_components.span, {
                    className: "hljs-tag",
                    children: ["<", _jsx(_components.span, {
                      className: "hljs-name",
                      children: "ConsentManager"
                    }), " ", _jsx(_components.span, {
                      className: "hljs-attr",
                      children: "writeKey"
                    }), "=", _jsx(_components.span, {
                      className: "hljs-string",
                      children: "\"WRITE_KEY\""
                    }), " />"]
                  }), "\n\n      ", _jsxs(_components.span, {
                    className: "hljs-tag",
                    children: ["<", _jsx(_components.span, {
                      className: "hljs-name",
                      children: "button"
                    }), " ", _jsx(_components.span, {
                      className: "hljs-attr",
                      children: "type"
                    }), "=", _jsx(_components.span, {
                      className: "hljs-string",
                      children: "\"button\""
                    }), " ", _jsx(_components.span, {
                      className: "hljs-attr",
                      children: "onClick"
                    }), "=", _jsx(_components.span, {
                      className: "hljs-string",
                      children: "{openConsentManager}"
                    }), ">"]
                  }), "\n        Website Data Collection Preferences\n      ", _jsxs(_components.span, {
                    className: "hljs-tag",
                    children: ["</", _jsx(_components.span, {
                      className: "hljs-name",
                      children: "button"
                    }), ">"]
                  }), "\n    ", _jsxs(_components.span, {
                    className: "hljs-tag",
                    children: ["</", _jsx(_components.span, {
                      className: "hljs-name",
                      children: "div"
                    }), ">"]
                  })]
                }), "\n  )\n}\n"]
              })
            })
          })]
        })]
      }), "\n", _jsx(_components.h2, {
        id: "api",
        children: "API"
      }), "\n", _jsxs(_components.p, {
        children: ["For a full list of features and options, see the ", _jsx(_components.a, {
          href: "https://github.com/ht-sdks/consent-manager",
          children: "README"
        }), "."]
      })]
    });
  }
}
export default MDXContent;
